
import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';

interface OnboardingLayoutProps {
  children: React.ReactNode;
  title: string;
  subtitle?: string;
  currentStep: number;
  totalSteps: number;
  onNext: () => void;
  onPrevious: () => void;
  nextDisabled?: boolean;
  nextLabel?: string;
}

const OnboardingLayout: React.FC<OnboardingLayoutProps> = ({
  children,
  title,
  subtitle,
  currentStep,
  totalSteps,
  onNext,
  onPrevious,
  nextDisabled = false,
  nextLabel = "Next"
}) => {
  const progressPercentage = ((currentStep) / totalSteps) * 100;
  
  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Header with logo */}
      <header className="border-b px-4 py-4">
        <div className="max-w-5xl mx-auto flex justify-between items-center">
          <div className="flex items-center">
            <h1 className="text-2xl font-bold text-primary">DirectPress</h1>
          </div>
          <span className="text-sm text-muted-foreground">
            Step {currentStep} of {totalSteps}
          </span>
        </div>
      </header>
      
      {/* Progress bar */}
      <div className="w-full px-4 py-2 bg-muted/20">
        <Progress value={progressPercentage} className="w-full" />
      </div>
      
      {/* Main content */}
      <main className="flex-1 flex flex-col max-w-5xl mx-auto w-full px-4 py-6">
        <div className="mb-6 text-center">
          <h2 className="text-2xl font-bold mb-2">{title}</h2>
          {subtitle && <p className="text-muted-foreground">{subtitle}</p>}
        </div>
        
        <div className="flex-1">
          {children}
        </div>
        
        {/* Navigation buttons */}
        <div className="mt-8 flex justify-between">
          <Button 
            variant="outline" 
            onClick={onPrevious}
            disabled={currentStep === 1}
          >
            <ChevronLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          
          <Button 
            onClick={onNext} 
            disabled={nextDisabled}
          >
            {nextLabel}
            {nextLabel === "Next" && <ChevronRight className="ml-2 h-4 w-4" />}
          </Button>
        </div>
      </main>
    </div>
  );
};

export default OnboardingLayout;
